import React from 'react';
import { Autocomplete, Chip, Grid, TextField, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { SnackContext } from 'src/store/ContextStore';
import { APICONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  ChangedField,
  useChangedFields
} from 'src/components/UI/input/changed-field';

export default function ExternalFamilyIds({
  user,
  onDirtyChange,
  // How many fields hold an unsaved change: 1 or 0 (TGCLOUD-1902).
  onChangeCount,
  onSaved,
  saveRef,
  discardRef
}) {
  const t = useAutoTranslation();

  const { setSnack } = React.useContext(SnackContext);

  const initialIds = React.useMemo(
    () =>
      Array.isArray(user?.externalFamilyIds) ? user.externalFamilyIds : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [ids, setIds] = React.useState(initialIds);
  // What Discard reverts to, and what the field is compared with (TGCLOUD-1902): seeded from the
  // loaded family, then RE-BASED on every successful save from the server's answer. The memo above
  // is frozen on mount, so without this "save ABC, then Discard All" put the field back to the
  // pre-save value while the server held ABC (TGCLOUD-1666). In ORDER: the first ID is the primary.
  const changes = useChangedFields({
    baseline: { externalFamilyIds: initialIds },
    values: { externalFamilyIds: ids },
    fields: ['externalFamilyIds'],
    compare: { externalFamilyIds: { arrays: 'sequence' } }
  });
  const { resetBaseline } = changes;
  React.useEffect(() => {
    onDirtyChange?.(changes.isChanged);
  }, [changes.isChanged]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    onChangeCount?.(changes.count);
  }, [changes.count]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (_, newValue) => {
    setIds(newValue);
  };

  const promoteToPrimary = (option) => {
    setIds((prev) => {
      if (!prev.includes(option) || prev[0] === option) return prev;
      const next = [option, ...prev.filter((id) => id !== option)];
      return next;
    });
  };

  const onSave = async () => {
    await axios
      .put(APICONSTANTS.setExternalFamilyIds.replace(':user_id', user._id), {
        externalFamilyIds: ids
      })
      .then(({ data }) => {
        // The route trims each entry and drops blanks, so the server's array is the truth — for the
        // field, for the Discard baseline, and for whatever the parent keeps in sync.
        const saved = data.externalFamilyIds;
        setIds(saved);
        resetBaseline({ externalFamilyIds: saved });
        onSaved?.(saved);
        setSnack({
          status: true,
          msg: t('External Family IDs updated'),
          severity: 'success'
        });
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('An error occurred'),
          severity: 'error'
        });
      });
  };

  const onDiscard = () => {
    setIds(changes.baseline.externalFamilyIds ?? initialIds);
  };

  React.useEffect(() => {
    if (saveRef) saveRef.current = onSave;
    if (discardRef) discardRef.current = onDiscard;
  });

  return (
    <Grid container spacing={1}>
      <ChangedField
        component={Grid}
        size={12}
        name="externalFamilyIds"
        changed={changes.changed.externalFamilyIds}
      >
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={ids}
          onChange={handleChange}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => {
              const isPrimary = index === 0;
              const tagProps = getItemProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={option}
                  label={option}
                  size="small"
                  icon={
                    isPrimary ? (
                      <StarIcon
                        sx={{
                          fontSize: 14,
                          color: '#1a7a8a !important'
                        }}
                      />
                    ) : (
                      <StarBorderIcon
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          promoteToPrimary(option);
                        }}
                        sx={{
                          fontSize: 14,
                          cursor: 'pointer',
                          color: 'rgba(0,0,0,0.4) !important',
                          '&:hover': { color: '#1a7a8a !important' }
                        }}
                      />
                    )
                  }
                  sx={{
                    backgroundColor: isPrimary
                      ? 'rgba(43, 169, 191, 0.18)'
                      : 'rgba(43, 169, 191, 0.08)',
                    color: '#1a7a8a',
                    fontSize: '0.75rem',
                    fontWeight: isPrimary ? 700 : 500,
                    borderRadius: '10px',
                    border: isPrimary
                      ? '1px solid rgba(43, 169, 191, 0.5)'
                      : '1px solid transparent',
                    '& .MuiChip-deleteIcon': {
                      color: '#1a7a8a',
                      '&:hover': { color: '#0f5a67' }
                    }
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('External Family IDs')}
              placeholder={t('Type an ID and press Enter')}
              size="small"
            />
          )}
        />
      </ChangedField>
      <Grid size={12}>
        <Typography
          sx={{
            fontSize: 11,
            color: 'text.secondary',
            lineHeight: 1.4
          }}
        >
          {t(
            'The starred ID is the primary alias. Click an outlined star to promote that ID — it becomes the headline when your display label mode is set to “Primary external ID”.'
          )}
        </Typography>
      </Grid>
    </Grid>
  );
}
